import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Youtube, Twitter, MessageCircle, Mail, Phone, MapPin } from "lucide-react";
import { BRAND, CATEGORIES } from "../../constants/brand";

export default function Footer() {
  return (
    <footer className="border-t border-neutral-200 bg-neutral-950 text-neutral-300" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="grid gap-10 md:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2" data-testid="footer-brand">
              <img src={BRAND.logo} alt="EarlyPrint" className="h-10 w-10 rounded-full" />
              <span className="font-display text-xl font-bold text-white">
                Early<span className="text-lime-400">Print</span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-neutral-400">{BRAND.tagline}</p>
            <p className="mt-3 text-xs text-neutral-500">
              {BRAND.legalName} · Serving {BRAND.location}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <a href={BRAND.social.instagram} target="_blank" rel="noopener noreferrer" className="rounded-full border border-neutral-800 p-2 hover:border-lime-400 hover:text-lime-400" aria-label="Instagram" data-testid="footer-instagram">
                <Instagram size={16} />
              </a>
              <a href={BRAND.social.facebook} target="_blank" rel="noopener noreferrer" className="rounded-full border border-neutral-800 p-2 hover:border-lime-400 hover:text-lime-400" aria-label="Facebook" data-testid="footer-facebook">
                <Facebook size={16} />
              </a>
              <a href={BRAND.social.linkedin} target="_blank" rel="noopener noreferrer" className="rounded-full border border-neutral-800 p-2 hover:border-lime-400 hover:text-lime-400" aria-label="LinkedIn" data-testid="footer-linkedin">
                <Linkedin size={16} />
              </a>
              <a href={BRAND.social.youtube} target="_blank" rel="noopener noreferrer" className="rounded-full border border-neutral-800 p-2 hover:border-lime-400 hover:text-lime-400" aria-label="YouTube" data-testid="footer-youtube">
                <Youtube size={16} />
              </a>
              <a href={BRAND.social.twitter} target="_blank" rel="noopener noreferrer" className="rounded-full border border-neutral-800 p-2 hover:border-lime-400 hover:text-lime-400" aria-label="X (Twitter)" data-testid="footer-twitter">
                <Twitter size={16} />
              </a>
              <a href={BRAND.social.whatsapp} target="_blank" rel="noopener noreferrer" className="rounded-full border border-neutral-800 p-2 hover:border-lime-400 hover:text-lime-400" aria-label="WhatsApp" data-testid="footer-whatsapp">
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-white">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-lime-400" data-testid="footer-about">About Us</Link></li>
              <li><Link to="/faqs" className="hover:text-lime-400" data-testid="footer-faqs">FAQs</Link></li>
              <li><Link to="/vendor/register" className="hover:text-lime-400" data-testid="footer-vendor">Register your shop</Link></li>
              <li><Link to="/contact" className="hover:text-lime-400" data-testid="footer-contact">Contact / Support</Link></li>
              <li><Link to="/terms" className="hover:text-lime-400" data-testid="footer-terms">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-lime-400" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/refund" className="hover:text-lime-400" data-testid="footer-refund">Refund & Escrow Policy</Link></li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-white">What We Print</h4>
            <ul className="grid grid-cols-1 gap-2 text-sm">
              {CATEGORIES.slice(0, 8).map((c) => (
                <li key={c.key}>
                  <Link to="/signup" className="hover:text-lime-400" data-testid={`footer-cat-${c.key}`}>{c.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-white">Get in touch</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Phone size={16} className="mt-1 text-lime-400" />
                <a href={`tel:${BRAND.phone}`} className="hover:text-lime-400" data-testid="footer-phone">{BRAND.phoneIntl}</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={16} className="mt-1 text-lime-400" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-lime-400" data-testid="footer-email">{BRAND.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-1 text-lime-400" />
                <span>{BRAND.location}</span>
              </li>
              <li className="flex items-start gap-2">
                <MessageCircle size={16} className="mt-1 text-lime-400" />
                <a href={BRAND.social.whatsapp} target="_blank" rel="noopener noreferrer" className="hover:text-lime-400" data-testid="footer-whatsapp-link">Chat on WhatsApp</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-neutral-800 pt-6 text-xs text-neutral-500 md:flex-row">
          <p>© {new Date().getFullYear()} {BRAND.legalName}. All rights reserved.</p>
          <p>Serving {BRAND.location} · UPI: {BRAND.upi.id}</p>
        </div>
      </div>
    </footer>
  );
}
